import { Component } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  ApplicationHostTypeService,
  ReferenceDataCategoriesConstants,
  ReferenceDataConstants,
  ReferenceDataLabels,
  ReferenceDataOptionLists,
  ReferenceDataService,
  RomeNotificationService,
  StatusResponse,
  ValidationErrors
} from 'rome-library';
import { UserCredentialStore } from '../../../interfaceTypes/sharedFeatures/registration/services';
import { States } from '../../../shared';
import { ExternalUserResetPasswordDto } from '../../models/external-user-reset-password-dto.model';
import { UserService } from '../../services/user.service';

export enum FormStep {
  Password = 'password',
  OtpCode = 'otpCode'
}

@UntilDestroy()
@Component({
  selector: 'boi-password-change-required',
  templateUrl: './password-change-required.page.html'
})
export class PasswordChangeRequiredPage {
  public resetPasswordDto = new ExternalUserResetPasswordDto();
  public readonly referenceDataLabels = ReferenceDataLabels;
  public readonly referenceDataOptionLists = ReferenceDataOptionLists;
  public readonly referenceDataConstants = ReferenceDataConstants;
  public readonly referenceDataCategoriesConstants = ReferenceDataCategoriesConstants;
  public errorMessage: string;
  public validationErrors: ValidationErrors;

  public readonly formStep = FormStep;
  public currentStep: FormStep = FormStep.Password;
  public isOtpInvalid: boolean;
  public isOtpSend: boolean;
  public otpCode: string;
  public hasExceededMaximumOtpSendLimit: boolean = false;
  public hasUserExceededMaximumOtpValidation: boolean = false;
  public hasOtpNotBeenSent: boolean = false;
  public hasAnOtpCodeActive: boolean = true;
  public blockResend: boolean = false;
  public maskedPhoneNumber: string;

  constructor(
    private readonly userService: UserService,
    private readonly notificationService: RomeNotificationService,
    private readonly referenceDataService: ReferenceDataService,
    private readonly userCredentialStore: UserCredentialStore,
    private readonly router: Router,
    private readonly applicationHostTypeService: ApplicationHostTypeService
  ) {}

  ngOnInit(): void {
    this.resetPasswordDto.OldPassword = this.userCredentialStore.getExpiredPassword();
    this.resetPasswordDto.EmailAddress = this.userCredentialStore.getEmail();

    if (!this.resetPasswordDto.OldPassword || !this.resetPasswordDto.EmailAddress) {
      this.goToLogin();
    }
  }

  public changeExpiredPassword(form: NgForm): void {
    if (form.valid) {
      this.userService
        .getSecurityChallengeForPasswordChangeRequest(this.resetPasswordDto)
        .pipe(untilDestroyed(this))
        .subscribe(
          (response) => {
            this.errorMessage = undefined;

            this.validationErrors = new ValidationErrors(response.ValidationErrors);
            if (response.Status === ReferenceDataOptionLists.DirectCustomerPasswordChangeAllowedStatus) {
              this.isOtpInvalid = false;
              this.changePassword();
              form.resetForm();
              return;
            }

            this.hasExceededMaximumOtpSendLimit = response.HasExceededMaximumOtpSendLimit;
            this.hasUserExceededMaximumOtpValidation = response.HasUserExceededMaximumOtpValidation;
            this.hasAnOtpCodeActive = response.HasAnOtpCodeActive;
            this.maskedPhoneNumber = response.MaskedPhoneNumber;

            if (response.Status === ReferenceDataOptionLists.DirectCustomerPasswordChangeTwoFACheckRequiredStatus) {
              if (this.hasExceededMaximumOtpSendLimit && this.hasUserExceededMaximumOtpValidation) {
                this.isOtpInvalid = true;
                this.currentStep = FormStep.OtpCode;
                return;
              }

              if (response.HasAnOtpCodeActive) {
                this.isOtpSend = true;
              }

              this.currentStep = FormStep.OtpCode;
              return;
            }
          },
          (error) => {}
        );
    } else {
      form.control.markAllAsTouched();
    }
  }

  private changePassword(): void {
    if (
      this.resetPasswordDto.NewPassword === this.resetPasswordDto.ConfirmedNewPassword &&
      this.resetPasswordDto.OldPassword &&
      this.resetPasswordDto.EmailAddress
    ) {
      this.userService
        .resetPassword(this.resetPasswordDto)
        .pipe(untilDestroyed(this))
        .subscribe(
          (response) => {
            this.handleResponse(response);
          },
          (errorResponse) => {
            this.errorMessage = errorResponse.error.Message;
          }
        );
    }
  }

  private handleResponse(response: StatusResponse): void {
    this.validationErrors = new ValidationErrors(response.ValidationErrors);

    if (response.Status === this.referenceDataOptionLists.UserRegistrationStatusSuccessful) {
      const message = this.referenceDataService.getLocalizeLabel(this.referenceDataLabels.NotificationSaveSuccess);
      this.notificationService.showInfo(message);

      this.userCredentialStore.setExpiredPassword(undefined);
      this.goToLogin();
    }
  }

  public goBack(): void {
    this.goToLogin();
  }

  private goToLogin(): void {
    void this.router.navigate([this.applicationHostTypeService.getCode(), States.Login]);
  }

  public sendCode(): void {
    this.userService
      .sendOtpCodeForPasswordChangeRequest(this.resetPasswordDto)
      .subscribe(
        (response: {
          HasExceededMaximumOtpSendLimit: boolean;
          HasOtpNotBeenSent: boolean;
          HasAnOtpCodeActive: boolean;
          HasUserExceededMaximumOtpValidation: boolean;
        }) => {
          this.hasExceededMaximumOtpSendLimit = response.HasExceededMaximumOtpSendLimit;

          this.isOtpSend = true;
          this.isOtpInvalid = false;
          this.hasOtpNotBeenSent = response.HasOtpNotBeenSent;
          this.hasAnOtpCodeActive = response.HasAnOtpCodeActive;
          this.hasUserExceededMaximumOtpValidation = response.HasUserExceededMaximumOtpValidation;

          if (response.HasOtpNotBeenSent) {
            this.blockResend = true;
          }
        }
      );
  }

  public validateOtpCode(form: NgForm): void {
    if (this.otpCode) {
      this.userService
        .validateOtpCodeForPasswordChangeRequest({
          OtpCode: this.otpCode,
          EmailAddress: this.resetPasswordDto.EmailAddress
        })
        .subscribe((response) => {
          if (response.IsValidOtp) {
            this.isOtpInvalid = false;
            form.resetForm();
            this.changePassword();
          } else {
            this.hasAnOtpCodeActive = response.HasAnOtpCodeActive;
            this.hasUserExceededMaximumOtpValidation = response.HasUserExceededMaximumOtpValidation;
            this.hasOtpNotBeenSent = false;

            if (response.HasUserExceededMaximumOtpValidation) {
              this.isOtpInvalid = false;
            } else {
              this.isOtpInvalid = true;
            }

            form.resetForm();
          }
        });
    }
  }
}
